// Account.tsx
import React, { createContext, useContext, PropsWithChildren, useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import { getAuth } from 'firebase/auth';
import AccountLayout from './Account/AccountLayout';
import axios from './utils/accountApi';
import { getAuthHeaders } from './utils/authHeaders';
import { message } from 'antd';

export interface AccountProps {
  isAuth: boolean;
  usersSharedState: any;
  setUsersSharedState: (v: any) => void;
  showLoginModal: () => void;
  authResolved: boolean;
}

export interface AccountContextValue {
  isAuth: boolean;
  usersSharedState: any;
  setUsersSharedState: (v: any) => void;
  showLoginModal: () => void;
  affiliateCode: string | null;
  hasReferralCode: boolean;
  referralStatusLoading: boolean;
  referralCodeInfo: ReferralCodeResponse | null;
  referralStatus: ReferralStatusResponse | null;
  refreshReferralStatus: () => Promise<void>;
  giftPurchases: GiftPurchase[];
  giftPurchasesLoading: boolean;
  refreshGiftPurchases: () => Promise<void>;
  giftPurchasesHasMore: boolean;
  giftPurchasesLoadingMore: boolean;
  loadMoreGiftPurchases: () => Promise<void>;
  isLangTesterUser: boolean;
  authResolved: boolean;
}

const AccountContext = createContext<AccountContextValue | null>(null);

const API_BASE = resolveFrontendApiBase();

interface ReferralCodeResponse {
  referralCode: string | null;
  hasCode: boolean;
  ownerUserId?: string;
  isCustom?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface ReferralRewardRow {
  planKey: string;
  planName: string;
  refereeBonus: number;
  affiliateMonthly: number;
  affiliateAnnual: number;
}

interface ReferralStatusResponse {
  appliedCode?: string | null;
  referrerUserId?: string | null;
  referrerDisplayName?: string | null;
  referralCreditsStatus?: string | null;
  referralCodeAppliedAt?: string | null;
  referralEligibilityLocked?: boolean;
  eligibility?: string;
  canApply?: boolean;
  ineligibleReason?: string | null;
  referralRewardChecked?: boolean;
  referralRewardResult?: string | null;
  referralRewardMatrix?: ReferralRewardRow[];
}

export interface GiftPurchase {
  id: string;
  token?: string;
  status: string;
  credits: number;
  quantity?: number;
  priceId?: string;
  productId?: string;
  planName?: string | null;
  buyerUserId?: string | null;
  buyerEmail?: string | null;
  currency?: string | null;
  amountTotal?: number | null;
  recipientEmail?: string | null;
  recipientName?: string | null;
  senderName?: string | null;
  message?: string | null;
  clientReferenceId?: string | null;
  checkoutSessionId?: string | null;
  claimedByUserId?: string | null;
  claimedByEmail?: string | null;
  claimedByDisplayName?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  claimedAt?: string | null;
}

export function useAccountContext(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error('useAccountContext must be used within <Account />');
  }
  return ctx;
}

function AccountProvider(props: PropsWithChildren<AccountProps>): JSX.Element {
  const { isAuth, usersSharedState, setUsersSharedState, showLoginModal, authResolved, children } = props;

  const userID: string | undefined = usersSharedState?.userID;
  const isLangTesterUser = Boolean(usersSharedState?.isLangTesterUser);
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);
  const [referralStatusLoading, setReferralStatusLoading] = useState<boolean>(false);
  const [referralCodeInfo, setReferralCodeInfo] = useState<ReferralCodeResponse | null>(null);
  const [referralStatus, setReferralStatus] = useState<ReferralStatusResponse | null>(null);
  const [giftPurchases, setGiftPurchases] = useState<GiftPurchase[]>([]);
  const [giftPurchasesLoading, setGiftPurchasesLoading] = useState<boolean>(false);
  const [giftPurchasesNextCursor, setGiftPurchasesNextCursor] = useState<string | null>(null);
  const [giftPurchasesLoadingMore, setGiftPurchasesLoadingMore] = useState<boolean>(false);
  const previousUserIdRef = useRef<string | undefined>(userID);

  const refreshReferralStatus = useCallback(async () => {
    if (!userID) {
      setAffiliateCode(null);
      setReferralCodeInfo(null);
      setReferralStatus(null);
      setReferralStatusLoading(false);
      return;
    }
    setReferralStatusLoading(true);
    try {
      const [codeRes, statusRes] = await Promise.all([
        axios.get<ReferralCodeResponse>(`${API_BASE}/referrals/my-code`, { params: { userID } }),
        axios.get<ReferralStatusResponse>(`${API_BASE}/referrals/status`, { params: { userID } })
      ]);
      const codeData = codeRes.data ?? null;
      const statusData = statusRes.data ?? null;
      setReferralCodeInfo(codeData);
      setAffiliateCode(codeData?.referralCode ?? null);
      setReferralStatus(statusData);
    } catch (_err) {
      setAffiliateCode(null);
      setReferralCodeInfo(null);
      setReferralStatus(null);
    } finally {
      setReferralStatusLoading(false);
    }
  }, [userID]);

  const refreshGiftPurchases = useCallback(async () => {
    if (!userID) {
      return;
    }
    const authInstance = getAuth();
    const currentUser = authInstance.currentUser;
    if (!currentUser) {
      return;
    }
    setGiftPurchasesLoading(true);
    try {
      const headers = await getAuthHeaders(currentUser);
      const response = await axios.get(`${API_BASE}/gifts/purchased`, { headers });
      const rawGifts = Array.isArray(response.data?.gifts) ? response.data.gifts : [];
      const parsed: GiftPurchase[] = rawGifts
        .filter((item: any) => item && typeof item === 'object')
        .map((item: any, index: number) => {
          const id =
            (item && typeof item.id === 'string' && item.id.trim()) ||
            (item && typeof item.checkoutSessionId === 'string' && item.checkoutSessionId.trim()) ||
            (item && typeof item.token === 'string' && item.token.trim()) ||
            `${userID}-gift-${index}`;
          return {
            id,
            ...item,
          } as GiftPurchase;
        });
      setGiftPurchases(parsed);
      setGiftPurchasesNextCursor(typeof response.data?.nextCursor === 'string' ? response.data.nextCursor : null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(
        typeof detail === 'string'
          ? detail
          : 'Failed to refresh your gift codes. Please try again.'
      );
    } finally {
      setGiftPurchasesLoading(false);
    }
  }, [userID]);

  const loadMoreGiftPurchases = useCallback(async () => {
    if (!userID || !giftPurchasesNextCursor || giftPurchasesLoadingMore) {
      return;
    }
    const authInstance = getAuth();
    const currentUser = authInstance.currentUser;
    if (!currentUser) {
      return;
    }
    setGiftPurchasesLoadingMore(true);
    try {
      const headers = await getAuthHeaders(currentUser);
      const response = await axios.get(`${API_BASE}/gifts/purchased`, {
        headers,
        params: { cursor: giftPurchasesNextCursor },
      });
      const rawGifts = Array.isArray(response.data?.gifts) ? response.data.gifts : [];
      const parsed: GiftPurchase[] = rawGifts
        .filter((item: any) => item && typeof item === 'object')
        .map((item: any, index: number) => {
          const id =
            (item && typeof item.id === 'string' && item.id.trim()) ||
            (item && typeof item.checkoutSessionId === 'string' && item.checkoutSessionId.trim()) ||
            (item && typeof item.token === 'string' && item.token.trim()) ||
            `${userID}-gift-loadMore-${index}`;
          return {
            id,
            ...item,
          } as GiftPurchase;
        });
      setGiftPurchases((prev) => {
        const existingIds = new Set(prev.map((gift) => gift.id));
        const merged = [...prev];
        parsed.forEach((gift) => {
          if (!existingIds.has(gift.id)) {
            merged.push(gift);
          }
        });
        return merged;
      });
      setGiftPurchasesNextCursor(typeof response.data?.nextCursor === 'string' ? response.data.nextCursor : null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      message.error(
        typeof detail === 'string'
          ? detail
          : 'Could not load older gift codes. Please try again.'
      );
    } finally {
      setGiftPurchasesLoadingMore(false);
    }
  }, [giftPurchasesNextCursor, giftPurchasesLoadingMore, userID]);

  useEffect(() => {
    refreshReferralStatus();
  }, [refreshReferralStatus]);

  useEffect(() => {
    refreshGiftPurchases();
  }, [refreshGiftPurchases]);

  useEffect(() => {
    const previous = previousUserIdRef.current;
    if (!userID && previous) {
      setGiftPurchases([]);
      setGiftPurchasesNextCursor(null);
      setGiftPurchasesLoading(false);
      setGiftPurchasesLoadingMore(false);
    } else if (userID && previous && previous !== userID) {
      setGiftPurchases([]);
      setGiftPurchasesNextCursor(null);
    }
    previousUserIdRef.current = userID;
  }, [userID]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(() => {
      refreshGiftPurchases();
    });
    return () => {
      unsubscribe();
    };
  }, [refreshGiftPurchases]);

  const value = useMemo<AccountContextValue>(() => {
    const hasCode = Boolean((referralCodeInfo?.referralCode || affiliateCode)?.trim());
    return {
      isAuth,
      usersSharedState,
      setUsersSharedState,
      showLoginModal,
      affiliateCode: affiliateCode,
      hasReferralCode: hasCode,
      referralStatusLoading,
      referralCodeInfo,
      referralStatus,
      refreshReferralStatus,
      giftPurchases,
      giftPurchasesLoading,
      refreshGiftPurchases,
      giftPurchasesHasMore: Boolean(giftPurchasesNextCursor),
      giftPurchasesLoadingMore,
      loadMoreGiftPurchases,
      isLangTesterUser,
      authResolved
    };
  }, [
    isAuth,
    usersSharedState,
    setUsersSharedState,
    showLoginModal,
    affiliateCode,
    referralStatusLoading,
    referralCodeInfo,
    referralStatus,
    refreshReferralStatus,
    giftPurchases,
    giftPurchasesLoading,
    refreshGiftPurchases,
    giftPurchasesNextCursor,
    giftPurchasesLoadingMore,
    loadMoreGiftPurchases,
    isLangTesterUser,
    authResolved
  ]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

const Account: React.FC<AccountProps> = (props) => {
  return (
    <AccountProvider {...props}>
      {/* AccountLayout includes the Tabs + <Outlet /> for subroutes */}
      <AccountLayout />
    </AccountProvider>
  );
};

export default Account;
